// Lighting the section you are actually in.
//
// The contents bar is a list of links; without this it is a list of links that
// never tells you where you are, which on a page of seventy-eight cards is
// most of the value gone.
//
// An IntersectionObserver rather than a scroll handler: the browser does the
// measuring, and it does not run a callback for every pixel of a thirty-screen
// page.
// The group each bar last followed the reader into. It starts as the first
// group, which is the one rendered open, so the reader's first scroll inside
// it does not count as arriving somewhere new.
const followed = new WeakMap();

function light(bar, seen) {
  // The topmost section currently on screen wins. Sorting by position rather
  // than by which fired last, because several cross the line together when a
  // fold is opened.
  const visible = [...seen.entries()]
    .filter(([, entry]) => entry.isIntersecting)
    .sort((a, b) => a[1].boundingClientRect.top - b[1].boundingClientRect.top);

  const current = visible[0]?.[0];
  bar.querySelectorAll("[data-onpage-link]").forEach((link) => {
    const here = link.dataset.onpageLink === current;
    link.classList.toggle("onpage__link--here", here);
    if (here) link.setAttribute("aria-current", "true");
    else link.removeAttribute("aria-current");
  });

  const currentLabel = bar.querySelector("[data-onpage-current]");
  const active = bar.querySelector("[aria-current='true'] span:first-child");
  if (currentLabel && active) currentLabel.textContent = active.textContent;

  // The rail is an accordion, but it follows the reader: when the active link
  // crosses into another GROUP, that group becomes the one open branch. Only
  // when it crosses. Re-syncing on every callback snapped a branch the reader
  // had opened by hand shut again: on a phone, tapping a branch scrolls the
  // page, the observer fires, and the rail put the old branch back.
  const activeGroup = active?.closest("[data-onpage-group]");
  if (!activeGroup || activeGroup === followed.get(bar)) return;
  followed.set(bar, activeGroup);
  if (!activeGroup.open) {
    bar.querySelectorAll("[data-onpage-group]").forEach((group) => {
      group.open = group === activeGroup;
    });
  }
}

// Pages that were not given a hand-written bar still have headings, and a
// heading is a section whether or not anybody labelled it as one. Rather than
// editing thirty views to say the same thing, a page with enough of them gets
// one built from what is already on it.
//
// Curated beats derived, so a page carrying `render "shared/on_this_page"`
// is left alone: those labels were chosen ("The seventy-two", "Ezekiel 1")
// and these are only the headings trimmed.
const MINIMUM = 3;
const LABEL_LIMIT = 32;

function slugify(text, index) {
  const base = text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  return base ? `section-${base}`.slice(0, 60) : `section-${index}`;
}

function buildBar() {
  const main = document.querySelector("main");
  // A page with an era strip already has its contents, along the top.
  if (!main || main.querySelector("[data-on-this-page], [data-era-strip]")) return;

  // A heading INSIDE a fold is not a place to send anybody: following the link
  // would land them on something shut.
  // Only the page's OWN sections belong here. An evidence card often has an
  // h2 whose text is a complete claim; the old broad selector promoted those
  // sentences into navigation and made the cure as sprawling as the page.
  // A <turbo-frame> is a WRAPPER, not a section of the page. `/source-books`
  // puts its twelve tradition groups inside one so a filter can replace the
  // list without reloading, and reading only `main.children` found no sections
  // at all there — the longest page on the site, twelve headings, and no
  // contents list. Look through the frame to what it holds.
  const containers = [...main.children]
    .flatMap((child) => (child.matches("turbo-frame") ? [...child.children] : [child]))
    .filter((child) => child.matches("section, article, figure, details.fold--section"));
  const headings = containers
    .map((container) => container.querySelector(
      ":scope > h2, :scope > header > h2, :scope > summary .fold--section__title, :scope > details > summary [role='heading'], :scope > div:first-child > h2"
    ))
    .filter(Boolean);
  if (headings.length < MINIMUM) return;

  const nav = document.createElement("nav");
  nav.className = "onpage";
  nav.setAttribute("aria-label", "On this page");
  nav.dataset.onThisPage = "";
  nav.dataset.testid = "on-this-page";
  nav.dataset.derived = "true";

  const disclosure = document.createElement("details");
  disclosure.className = "onpage__disclosure";

  const trigger = document.createElement("summary");
  trigger.className = "onpage__trigger";
  trigger.innerHTML = `<span><span class="onpage__label">On this page</span><span class="onpage__current" data-onpage-current></span></span><span class="onpage__chevron" aria-hidden="true"></span>`;
  disclosure.appendChild(trigger);

  const content = document.createElement("div");
  content.className = "onpage__content";
  const desktopLabel = document.createElement("p");
  desktopLabel.className = "onpage__desktop-label";
  desktopLabel.setAttribute("aria-hidden", "true");
  desktopLabel.textContent = "On this page";
  content.appendChild(desktopLabel);

  const list = document.createElement("ul");
  list.className = "onpage__list";

  headings.forEach((heading, index) => {
    // The section, or the heading itself when it is not wrapped in one.
    const target = heading.closest("section, article, figure") || heading;
    if (!target.id) target.id = slugify(heading.textContent, index);

    const item = document.createElement("li");
    const link = document.createElement("a");
    link.href = `#${target.id}`;
    link.dataset.onpageLink = target.id;
    // Not a Turbo visit — see shared/_on_this_page.html.erb.
    link.dataset.turbo = "false";
    const text = heading.textContent.trim().replace(/\s+/g, " ");
    const label = document.createElement("span");
    label.textContent = text.length > LABEL_LIMIT ? `${text.slice(0, LABEL_LIMIT - 1)}…` : text;
    if (label.textContent !== text) link.title = text;
    link.appendChild(label);
    item.appendChild(link);
    list.appendChild(item);
  });

  content.appendChild(list);
  trigger.querySelector("[data-onpage-current]").textContent =
    list.querySelector("a span")?.textContent || "Sections";
  disclosure.appendChild(content);
  nav.appendChild(disclosure);
  // After whatever the page opens with, so the bar does not come before the
  // title of the thing it is a contents list for.
  const header = main.querySelector(":scope > header");
  if (header) header.after(nav); else main.prepend(nav);
}

function revealTarget(id) {
  const target = id && document.getElementById(id);
  if (!target) return;

  // A contents link never appears to do nothing just because its destination
  // is inside a folded reference section.
  target.closest("details")?.setAttribute("open", "");
  target.querySelector(":scope > details")?.setAttribute("open", "");
}

export function start() {
  buildBar();

  if (typeof document === "undefined" || !("IntersectionObserver" in window)) return;

  document.querySelectorAll("[data-on-this-page]").forEach((bar) => {
    if (bar.dataset.onpageReady === "true") return;
    bar.dataset.onpageReady = "true";
    followed.set(bar, bar.querySelector("[data-onpage-group]"));
    const main = bar.closest("main");
    main?.classList.add("has-onpage");
    const seen = new Map();
    const disclosure = bar.querySelector(".onpage__disclosure");
    const wide = window.matchMedia("(min-width: 1180px)");
    const syncDisclosure = () => {
      if (disclosure) disclosure.open = wide.matches;
    };
    syncDisclosure();
    wide.addEventListener("change", syncDisclosure);
    const ids = [...bar.querySelectorAll("[data-onpage-link]")]
      .map((link) => link.dataset.onpageLink);

    bar.querySelectorAll("[data-onpage-group]").forEach((group) => {
      group.addEventListener("toggle", () => {
        if (!group.open) return;
        bar.querySelectorAll("[data-onpage-group]").forEach((sibling) => {
          if (sibling !== group) sibling.open = false;
        });
      });
    });

    bar.addEventListener("click", (event) => {
      const link = event.target.closest("[data-onpage-link]");
      if (!link) return;
      revealTarget(link.dataset.onpageLink);
      if (!wide.matches) disclosure?.removeAttribute("open");
    });

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => seen.set(entry.target.id, entry));
      light(bar, seen);
    }, {
      // The band the eye is actually reading: ignore what is scrolling past
      // the very top and what has not arrived yet.
      rootMargin: "-15% 0px -70% 0px",
      threshold: 0
    });

    ids.forEach((id) => {
      const section = document.getElementById(id);
      if (section) observer.observe(section);
    });
  });

  revealTarget(window.location.hash.slice(1));
}

window.addEventListener("hashchange", () => revealTarget(window.location.hash.slice(1)));

document.addEventListener("DOMContentLoaded", start);
document.addEventListener("turbo:load", start);

// A frame that has just been replaced holds different sections — on
// `/source-books` a filter changes which traditions are on the page at all —
// so a bar derived from the old ones is a list of links to things that have
// gone. Only a DERIVED bar is rebuilt: one the server rendered is the server's
// to say, and `data-onpage-ready` keeps the rest of start() idempotent.
document.addEventListener("turbo:frame-load", (event) => {
  const main = event.target.closest("main");
  if (!main) return;
  const derived = main.querySelector('[data-on-this-page][data-derived="true"]');
  if (!derived && main.querySelector("[data-on-this-page]")) return;
  derived?.remove();
  // Dropped too, or a page whose new filter leaves fewer than MINIMUM groups
  // keeps a two-column grid with nothing in the second column.
  main.classList.remove("has-onpage");
  start();
});
